import React, { FC, useEffect, useRef, useState, MouseEvent } from "react";
import { useSearchParams } from "react-router-dom";

interface LandingProps {}

const Landing: FC<LandingProps> = () => {
  const [searchParams] = useSearchParams();
  const rawSchemeUrl = (searchParams.get("urlredirect") || "").trim(); // e.g. myhyundaiindonesia://ServiceBooking/
  const [message, setMessage] = useState<string>("");

  const fallbackTimerRef = useRef<number | null>(null);
  const becameHiddenRef = useRef<boolean>(false);

  // ----------------- Utilities -----------------

  const getMobileOS = (): "ios" | "android" | "unknown" => {
    const ua = navigator.userAgent || navigator.vendor || "";
    if (/android/i.test(ua)) return "android";
    if (/iPad|iPhone|iPod/i.test(ua)) return "ios";
    const isIpadOS =
      navigator.platform === "MacIntel" && (navigator as any).maxTouchPoints > 1;
    if (isIpadOS) return "ios";
    return "unknown";
  };

  const validSchemes = ["myhyundaiindonesia://", "mbluelinkid://"];

  const isValidScheme = (url: string): boolean => {
    if (!url) return false;
    const lower = url.toLowerCase();
    const allowed = validSchemes.some((s) => url.startsWith(s));
    if (!allowed) return false;
    if (lower.startsWith("javascript:") || lower.startsWith("data:")) return false;
    return true;
  };

  /**
   * Your RN linking map says: ServiceBooking/:vin? → optional VIN.
   * If no VIN is present, remove a solitary trailing slash after the path.
   * Examples:
   *  myhyundaiindonesia://ServiceBooking/  -> myhyundaiindonesia://ServiceBooking
   *  myhyundaiindonesia://ServiceBooking   -> unchanged
   *  myhyundaiindonesia://ServiceBooking/ABC123 -> unchanged
   */
  const normalizeOptionalTrailingSlash = (url: string): string => {
    // Remove the trailing slash only if it is the last char of the URL (no query/hash)
    // and there is at least one non-empty path segment after the scheme.
    return url.replace(/^(.*?:\/\/[^/]+(?:\/[^/?#]+))\/(\?|#|$)/, "$1$2");
  };

  const normalize = (url: string): string => {
    const cleaned = normalizeOptionalTrailingSlash(url.trim());
    try {
      // encodeURI keeps :, /, ?, & but encodes spaces and invalid characters
      return encodeURI(cleaned);
    } catch {
      return cleaned;
    }
  };

  const getStoreUrl = (schemeUrl: string, os: "ios" | "android" | "unknown") => {
    if (os === "android") {
      if (schemeUrl.startsWith("myhyundaiindonesia://")) {
        return "https://play.google.com/store/apps/details?id=id.co.hyundaimotor.mmp";
      }
      if (schemeUrl.startsWith("mbluelinkid://")) {
        return "https://play.google.com/store/apps/details?id=com.hyundai.bluelink.idn&hl=id";
      }
      return "";
    } else if (os === "ios") {
      // Use itms-apps:// to jump straight to App Store app
      if (schemeUrl.startsWith("myhyundaiindonesia://")) {
        return "itms-apps://itunes.apple.com/app/id1580588669";
      }
      if (schemeUrl.startsWith("mbluelinkid://")) {
        return "itms-apps://itunes.apple.com/app/id1603033485";
      }
      return "";
    }
    return "";
  };

  const goToStore = ({
                       schemeUrl,
                       os,
                       fromUserGesture,
                     }: {
    schemeUrl: string;
    os: "ios" | "android" | "unknown";
    fromUserGesture: boolean;
  }) => {
    const storeUrl = getStoreUrl(schemeUrl, os) || getStoreUrl("myhyundaiindonesia://", os);
    if (!storeUrl) {
      setMessage("Failed to redirect. Please install the app manually.");
      return;
    }
    try {
      // Prefer doing store nav inside a gesture on iOS (more reliable),
      // but for auto-launch parity we use assign directly in fallback as well.
      window.location.assign(storeUrl);
    } catch {
      window.location.href = storeUrl;
    }
  };

  // ----------------- Main Action -----------------

  const openPage = (e?: MouseEvent<HTMLElement>) => {
    const os = getMobileOS();
    const fromUserGesture = !!e;

    setMessage("");
    becameHiddenRef.current = false;

    if (!isValidScheme(rawSchemeUrl)) {
      // Invalid → jump to store directly (avoid Safari "invalid address" for malformed URLs)
      goToStore({ schemeUrl: "myhyundaiindonesia://", os, fromUserGesture });
      return;
    }

    const deeplink = normalize(rawSchemeUrl); // removes trailing slash if only ServiceBooking/
    const storeUrl = getStoreUrl(deeplink, os);

    const cancelFallback = () => {
      becameHiddenRef.current = true;
      if (fallbackTimerRef.current !== null) {
        window.clearTimeout(fallbackTimerRef.current);
        fallbackTimerRef.current = null;
      }
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("pagehide", onPageHide);
      window.removeEventListener("blur", onBlur);
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") cancelFallback();
    };
    const onPageHide = () => cancelFallback();
    const onBlur = () => {
      // iOS fires blur aggressively even when app didn't open → don't cancel on iOS
      if (os !== "ios" && !becameHiddenRef.current) cancelFallback();
    };

    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("pagehide", onPageHide);
    if (os !== "ios") window.addEventListener("blur", onBlur);

    try {
      // **AUTO-LAUNCH on both Android and iOS**
      // Direct navigation gives the best chance to open the app if installed.
      window.location.href = deeplink;
    } catch {
      // no-op
    }

    // Fallback to Store if app didn't open
    fallbackTimerRef.current = window.setTimeout(() => {
      if (!becameHiddenRef.current) {
        if (storeUrl) {
          goToStore({ schemeUrl: deeplink, os, fromUserGesture });
        } else {
          setMessage("Failed to redirect. Please install the app manually.");
        }
      }
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("pagehide", onPageHide);
      window.removeEventListener("blur", onBlur);
      if (fallbackTimerRef.current !== null) {
        window.clearTimeout(fallbackTimerRef.current);
        fallbackTimerRef.current = null;
      }
    }, os === "ios" ? 2200 : 1800);
  };

  // ----------------- Lifecycle -----------------

  useEffect(() => {
    if (!rawSchemeUrl) return;
    // **AUTO-RUN on page load for BOTH Android and iOS**
    openPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawSchemeUrl]);

  // ----------------- UI -----------------

  return (
    <div className="flex flex-col justify-center items-center h-screen bg-background p-4">
      <div className="flex flex-col justify-center items-center mb-4">
        <h1 className="text-lg text-center text-black hyundaiTextHead mb-2">
          Redirecting to myHyundai app...
        </h1>
        <p className="text-sm text-center">
          If nothing happened,{" "}
          <button
            type="button"
            className="cursor-pointer text-secondary underline"
            onClick={(e) => openPage(e)}
          >
            tap here
          </button>{" "}
          to open the app or install it.
        </p>
      </div>
      {message && <p className="text-sm text-center text-danger">{message}</p>}
    </div>
  );
};

export default Landing;